import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Server, ShieldCheck, Cpu, Check, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Consultation: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <SEO 
        title="XCape SDK for Enterprise - Location Intelligence for HR Apps"
        description="Integrasikan teknologi XCape Location Core ke dalam aplikasi HR dan Absensi Anda. Deteksi Fake GPS server-side, validasi hardware, dan optimasi sinyal."
      />

      {/* Hero Section */}
      <section className="bg-brand-dark relative overflow-hidden mb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-slate-900 text-brand-primary text-xs font-mono font-bold border border-slate-800">
            <Code className="h-3 w-3" /> FOR DEVELOPERS & HR ISV
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
            Build Better <span className="text-brand-primary">Location Intelligence</span>
          </h1>
          <p className="text-xl text-slate-500 max-w-2xl mx-auto mb-8">
            Jangan biarkan Fake GPS murahan merusak integritas data absensi Anda. Integrasikan **XCape Location Core** untuk validasi hardware-level di sisi client dan server.
          </p>
          <div className="flex justify-center gap-4">
            <a href="#integration" className="px-8 py-3 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 transition-colors">
              Lihat Dokumentasi
            </a>
            <Link to="/hubungi-kami" className="px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors">
              Hubungi Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-8 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-lg transition-all">
             <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6 text-blue-600">
                <ShieldCheck className="h-6 w-6" />
             </div>
             <h3 className="text-xl font-bold text-slate-900 mb-3">Anti-Mock Detection</h3>
             <p className="text-slate-600 text-sm">
                Deteksi penggunaan `ALLOW_MOCK_LOCATION` developer option, Magisk Module, dan Xposed Framework signatures dengan akurasi 99.8%.
             </p>
          </div>
          <div className="p-8 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-lg transition-all">
             <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6 text-purple-600">
                <Cpu className="h-6 w-6" />
             </div>
             <h3 className="text-xl font-bold text-slate-900 mb-3">Hardware Fingerprinting</h3>
             <p className="text-slate-600 text-sm">
                Validasi konsistensi antara data GPS, Cell Tower ID, dan Wi-Fi SSID untuk memastikan user benar-benar berada di lokasi fisik.
             </p>
          </div>
          <div className="p-8 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-lg transition-all">
             <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6 text-green-600">
                <Server className="h-6 w-6" />
             </div>
             <h3 className="text-xl font-bold text-slate-900 mb-3">Server-Side Validation API</h3>
             <p className="text-slate-600 text-sm">
                Webhook endpoint untuk memvalidasi payload lokasi sebelum disimpan ke database Anda. Filter koordinat "Jumping" secara otomatis.
             </p>
          </div>
        </div>
      </div>

      {/* Code Demo Section */}
      <section id="integration" className="bg-slate-900 py-20 text-white">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                    <h2 className="text-3xl font-bold mb-6">Integrasi Semudah Copy-Paste</h2>
                    <p className="text-slate-400 mb-8">
                        SDK kami tersedia untuk Flutter, React Native, dan Native Android. Cukup inisialisasi pada root aplikasi Anda.
                    </p>
                    
                    <ul className="space-y-4 mb-8">
                        {[
                            "Supports Android 8.0 - 15.0",
                            "Minimal Battery Drain (< 1%)",
                            "Offline Validation Cache",
                            "GDPR & PDP Compliant (No Personal Data Logs)"
                        ].map((item, i) => (
                            <li key={i} className="flex items-center gap-3">
                                <div className="h-6 w-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <Check className="h-3 w-3 text-green-500" />
                                </div>
                                <span className="text-slate-300">{item}</span>
                            </li>
                        ))}
                    </ul>

                    <Link to="/hubungi-kami" className="text-brand-primary font-bold hover:text-white transition-colors flex items-center gap-2">
                        Minta Akses SDK Beta <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                
                {/* Code Block */}
                <div className="bg-slate-950 rounded-xl p-6 font-mono text-sm border border-slate-800 shadow-2xl relative">
                    <div className="absolute top-4 right-4 flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                    </div>
                    <div className="text-slate-500 mb-4">// React Native Implementation Example</div>
                    
                    <div className="space-y-1">
                        <p><span className="text-purple-400">import</span> XCape <span className="text-purple-400">from</span> <span className="text-green-400">'@xcape/location-core'</span>;</p>
                        <br />
                        <p><span className="text-slate-500">// Initialize Guard</span></p>
                        <p><span className="text-purple-400">await</span> XCape.init({'{'}</p>
                        <p className="pl-4">apiKey: <span className="text-green-400">'YOUR_ENTERPRISE_KEY'</span>,</p>
                        <p className="pl-4">strictMode: <span className="text-blue-400">true</span>,</p>
                        <p className="pl-4">onMockDetected: (event) ={'>'} {'{'}</p>
                        <p className="pl-8"><span className="text-yellow-400">console</span>.warn(<span className="text-green-400">"Fake GPS detected!"</span>, event.type);</p>
                        <p className="pl-8"><span className="text-slate-500">// Auto-flag user account</span></p>
                        <p className="pl-8">api.flagUser(event.userId);</p>
                        <p className="pl-4">{'}'}</p>
                        <p>{'}'});</p>
                        <br />
                        <p><span className="text-slate-500">// Get Validated Coordinates</span></p>
                        <p><span className="text-purple-400">const</span> location = <span className="text-purple-400">await</span> XCape.getCurrentPosition();</p>
                        <p><span className="text-purple-400">if</span> (location.isSecure) {'{'}</p>
                        <p className="pl-4">submitAttendance(location.coords);</p>
                        <p>{'}'}</p>
                    </div>
                </div>
            </div>
         </div>
      </section>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 mt-20 text-center">
         <h2 className="text-3xl font-bold text-slate-900 mb-6">Siap Meningkatkan Akurasi Aplikasi Anda?</h2>
         <p className="text-slate-500 mb-8">
            Konsultasikan arsitektur sistem absensi Anda dengan engineer kami. Kami menyediakan layanan audit keamanan lokasi gratis untuk startup.
         </p>
         <Link to="/hubungi-kami" className="inline-block px-10 py-4 bg-brand-primary text-white rounded-full font-bold shadow-lg hover:bg-orange-600 transition-all">
            Jadwalkan Konsultasi Teknis
         </Link>
      </div>
    </div>
  );
};

export default Consultation;